import { createColumnHelper } from "@tanstack/react-table";
import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonToolbar,
  Col,
  Form,
  ProgressBar,
  Row,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type {
  MoveOutPreview,
  MoveOutRequest,
  SecurityDetails as SecurityDetailsData,
  SecurityHoldingAccount,
  WeightedName,
} from "../../../api/src/security-details/security-details.types";
import type { ChartPeriodKey } from "../../../api/src/security-details/market-statistics";
import type { Transaction } from "../../../api/src/transactions/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import HelpTooltip from "../components/HelpTooltip";
import SummaryPill from "../components/SummaryPill";
import { Table as DataTable, useTable } from "../components/Table/Table";
import { TabContainer } from "../components/TabContainer";
import TransactionsTable from "../components/TransactionsTable";
import { extractServerMessage } from "../lib/api";
import { useRolesResolution } from "../lib/auth";
import { toISODateNoTimezone } from "../lib/date";
import {
  formatCurrency,
  formatCurrencyComponent,
  formatPercent,
} from "../lib/numbers";
import {
  assetClassRiskPath,
  categoryAssetClassPath,
  securityMasterRowPath,
} from "../lib/security";
import { useQueryOpenPositionSwaps } from "../rebalances/positionSwapsLib";
import {
  MOVE_OUT_REASON_LABELS,
  categoryWrite,
  useCreatePositionSwap,
  usePreviewMoveOut,
  useQueryAssetCategories,
  useQuerySecurityDetails,
  useQuerySecurityMarketData,
  useQuerySecurityTransactions,
  useUpdateSecurityCategory,
} from "./lib";
import {
  FUND_VARIANTS,
  HeaderPrice,
  type MarketDataQuery,
  OverviewMarketData,
} from "./MarketDataPanels";
import PositionsTab from "./PositionsTab";
import SecurityChart, { activePeriod } from "./SecurityChart";

const VARIANT_LABELS: Record<
  SecurityDetailsData["security"]["variant"],
  string
> = {
  stock: "Stock",
  fund: "Fund",
  bond_fund: "Bond fund",
  money_market: "Money market",
  bond: "Bond",
};

const TREATMENT_LABELS: Record<string, string> = {
  like_primary: "Trade like primary",
  hold_only: "Hold only",
  sell_no_tax: "Sell without tax impact",
  exclude: "Exclude",
};

/** A percent from 0 to 100, as the API sends it. */
const pct100 = (value: number | null, precision = 1) =>
  value === null ? "—" : formatPercent(value / 100, precision);

const dateText = (value: Date | null) =>
  value ? value.toLocaleDateString() : "—";

/** The `?tab=` of the page; Overview when it names no other tab. */
const TAB_PARAM = "tab";
type SecurityTab = "overview" | "positions" | "activity";

const SecurityDetails = () => {
  const { symbol = "" } = useParams();
  const { data, isLoading, isError } = useQuerySecurityDetails(symbol);
  // APP-1487: the FastTrack data; never blocks the page's other sections.
  const marketData = useQuerySecurityMarketData(symbol);
  const [searchParams, setSearchParams] = useSearchParams();
  // Counts the clicks on the swap button; each one brings the swap section
  // into view, also when the Overview tab is already open.
  const [moveOutOpened, setMoveOutOpened] = useState(0);
  // The chart period the advisor selected; the risk and drawdown panels show
  // the same period (APP-1487 R21, R25). The route keeps this component mounted
  // from one symbol to the next, so a selection holds only for its own symbol,
  // and a security opens on the default period each time it is opened (R52).
  const [chartSelection, setChartSelection] = useState<{
    symbol: string;
    period: ChartPeriodKey;
  } | null>(null);
  if (chartSelection && chartSelection.symbol !== symbol) {
    // Drop the selection of the symbol left, so returning to it opens on the
    // default period again rather than on the old selection.
    setChartSelection(null);
  }
  const chartPeriod =
    chartSelection?.symbol === symbol ? chartSelection.period : null;
  const setChartPeriod = (period: ChartPeriodKey) =>
    setChartSelection({ symbol, period });
  // APP-1487 R47, R48: only a fund variant has the Positions tab.
  const hasPositions = !!data && FUND_VARIANTS.includes(data.security.variant);
  const tabParam = searchParams.get(TAB_PARAM);
  const activeTab: SecurityTab =
    tabParam === "activity"
      ? "activity"
      : tabParam === "positions" && hasPositions
        ? "positions"
        : "overview";
  const selectTab = (key: string | null) => {
    if (
      key !== "overview" &&
      key !== "activity" &&
      !(key === "positions" && hasPositions)
    ) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set(TAB_PARAM, key);
    setSearchParams(next, { replace: true });
  };

  if (isLoading) return <Loading />;
  if (isError || !data) {
    return (
      <Alert variant="danger">
        {symbol} could not be loaded. It may not be in your firm's securities.
      </Alert>
    );
  }

  // APP-1486 page R27: every variant has the Overview and Activity tabs, and
  // a fund variant has the Positions tab between them (APP-1487 R47). The
  // Activity tab mounts on its first opening, so the page asks for the
  // transactions only when the advisor looks at them.
  // The header, tabs and tab panel follow the household page
  // (`HouseholdHeader`, `TabContainer`): one panel under the tabs, with the
  // sections inside it under plain headings.
  return (
    <>
      <Header
        details={data}
        marketData={marketData}
        onMoveOut={() => {
          selectTab("overview");
          setMoveOutOpened((n) => n + 1);
        }}
      />
      <Tabs
        id="security-tabs"
        activeKey={activeTab}
        onSelect={selectTab}
        mountOnEnter
      >
        <Tab eventKey="overview" title="Overview">
          <TabContainer>
            <Row className="g-4">
              <Col xs={12}>
                <SecurityChart
                  symbol={data.security.symbol}
                  isBond={data.security.variant === "bond"}
                  marketData={marketData}
                  selected={chartPeriod}
                  onSelect={setChartPeriod}
                />
              </Col>
              <OverviewMarketData
                variant={data.security.variant}
                securityType={data.security.securityType}
                marketData={marketData}
                period={activePeriod(marketData, chartPeriod)}
              />
              <Col lg={6}>
                <HeldAtFirm details={data} />
              </Col>
              <Col lg={6}>
                <Classification details={data} />
              </Col>
              <Col xs={12}>
                <Holders holdings={data.holdings} />
              </Col>
              <Col xs={12}>
                <Configuration details={data} />
              </Col>
              {/* Individual bonds don't qualify for a position swap. Keyed by
                  symbol so a cached security's page starts a fresh form and
                  preview, never the last security's. */}
              {data.security.variant !== "bond" && (
                <Col xs={12}>
                  <MoveOut
                    key={data.security.symbol}
                    details={data}
                    opened={moveOutOpened}
                  />
                </Col>
              )}
            </Row>
          </TabContainer>
        </Tab>
        {hasPositions && (
          <Tab eventKey="positions" title="Positions">
            <TabContainer>
              <PositionsTab
                variant={data.security.variant}
                marketData={marketData}
              />
            </TabContainer>
          </Tab>
        )}
        <Tab eventKey="activity" title="Activity">
          <TabContainer>
            {/* Keyed by symbol so another security's page starts again
                from the default dates. */}
            <Activity
              key={data.security.symbol}
              symbol={data.security.symbol}
            />
          </TabContainer>
        </Tab>
      </Tabs>
    </>
  );
};

/**
 * One label and value of a summary list, laid out as the household page's
 * Account Summary and Tax Summary: the bold label beside its value. Use it
 * inside a `<dl>`.
 */
const SummaryItem = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => (
  <Row>
    <Col as="dt" xxl={5} sm={6}>
      {label}
    </Col>
    <Col as="dd">{children}</Col>
  </Row>
);

const Header = ({
  details,
  marketData,
  onMoveOut,
}: {
  details: SecurityDetailsData;
  marketData: MarketDataQuery;
  /** Opens the swap section of the Overview tab. */
  onMoveOut: () => void;
}) => {
  const { security, classification, holdingsSummary } = details;
  return (
    <>
      {security.variant !== "bond" && (
        <ButtonToolbar className="mb-3 justify-content-end gap-2">
          <ActionButton
            variant="secondary"
            label="Move clients out of this security"
            icon="/icons/sell.svg"
            decorativeIcon
            type="button"
            className="btn-action_secondary_wide"
            onClick={onMoveOut}
          />
        </ButtonToolbar>
      )}
      <Row>
        <Col
          xs={12}
          className="d-flex align-items-center justify-content-between flex-wrap"
        >
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <div className="me-3">
              <h2 className="fs-3 fw-semibold lh-1 mb-1">{security.symbol}</h2>
              <span className="text-muted">{security.description}</span>
              {/* Page R24, item 7: a stock's sector, industry and country. */}
              {security.variant === "stock" &&
                (security.sector || security.industry || security.country) && (
                  <div className="small text-muted mt-1">
                    Sector {security.sector ?? "—"} · Industry{" "}
                    {security.industry ?? "—"} · Country{" "}
                    {security.country ?? "—"}
                  </div>
                )}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Badge bg="primary">{VARIANT_LABELS[security.variant]}</Badge>
              {security.securityType && (
                <Badge bg="secondary">{security.securityType}</Badge>
              )}
              {security.cusip && (
                <Badge bg="light" text="dark" className="font-monospace">
                  CUSIP {security.cusip}
                </Badge>
              )}
              {security.is13f !== null && (
                <Badge
                  bg={security.is13f ? "success" : "light"}
                  text={security.is13f ? undefined : "dark"}
                >
                  {security.is13f ? "13-F reportable" : "Not 13-F reportable"}
                </Badge>
              )}
              {classification.isUncategorized && (
                <Badge bg="danger">Uncategorized</Badge>
              )}
            </div>
          </div>
          <div className="d-flex align-items-start flex-wrap">
            <HeaderPrice marketData={marketData} />
            <SummaryPill
              label="Market value"
              value={formatCurrency(holdingsSummary.marketValue)}
              size="sm"
              className="me-3 mb-3"
            />
            <SummaryPill
              label="Unrealized gain/loss"
              value={formatCurrency(holdingsSummary.unrealizedGainLoss)}
              size="sm"
              className="mb-3"
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

/** A summary label with a help icon whose tooltip says what the value means. */
const ExplainedLabel = ({ label, help }: { label: string; help: string }) => (
  <span className="d-inline-flex align-items-center gap-1">
    {label}
    <HelpTooltip
      tooltip={help}
      buttonProps={{ width: 16, alt: `What ${label.toLowerCase()} means` }}
    />
  </span>
);

const HeldAtFirm = ({ details }: { details: SecurityDetailsData }) => {
  const s = details.holdingsSummary;
  const flags: string[] = [];
  if (s.washSaleAccounts > 0) {
    flags.push(
      `Wash sales: ${s.washSaleAccounts} accounts hold lots with ${formatCurrency(s.disallowedLoss)} of disallowed loss.`,
    );
  }
  if (s.costBasisIncompleteAccounts > 0) {
    flags.push(
      `Cost basis incomplete in ${s.costBasisIncompleteAccounts} accounts. Gain and loss figures understate those positions.`,
    );
  }
  if (details.classification.isUncategorized) {
    flags.push(
      "This security has no category, so it maps to no asset class and households that hold it cannot be rebalanced.",
    );
  }
  return (
    <section>
      <h3 className="h4">
        Held in client accounts{" "}
        <small className="fs-6 fw-normal text-muted">
          as of {dateText(s.asOfDate)}
        </small>
      </h3>
      <dl className="mb-0">
        <SummaryItem label="Market value">
          {formatCurrency(s.marketValue)}{" "}
          <span className="text-muted">
            ({pct100(s.percentOfFirmAum, 2)} of firm AUM)
          </span>
        </SummaryItem>
        <SummaryItem label="Accounts / households">
          {s.accounts} / {s.households}
        </SummaryItem>
        <SummaryItem label="Unrealized gain/loss">
          {formatCurrencyComponent(s.unrealizedGainLoss)}
        </SummaryItem>
        <SummaryItem label="Harvestable loss (taxable only)">
          {formatCurrencyComponent(s.harvestableLoss)}
          <div className="small text-muted">
            in {s.harvestableLossAccounts} taxable accounts · {s.lotsAtLoss} of{" "}
            {s.lots} lots at a loss
          </div>
        </SummaryItem>
        <SummaryItem label="Average cost">
          {s.averageCost === null ? "—" : formatCurrency(s.averageCost, 2)}
        </SummaryItem>
        <SummaryItem
          label={
            <ExplainedLabel
              label="Median weight"
              help="Half the accounts holding this security have less of their value in it than this, and half have more. A median, so a few small accounts holding almost nothing else don't pull it up."
            />
          }
        >
          {pct100(s.medianPercentOfAccount, 1)}
        </SummaryItem>
        <SummaryItem
          label={
            <ExplainedLabel
              label="Largest position"
              help="The largest dollar amount of this security held in any one account at your firm. The percentage is how much of that one account's value this holding makes up."
            />
          }
        >
          {s.largestPosition
            ? `${formatCurrency(s.largestPosition.value)} · ${pct100(s.largestPosition.percentOfAccount, 1)} of its account`
            : "—"}
        </SummaryItem>
        <SummaryItem label="In models">
          {details.configuration.models.length}
        </SummaryItem>
      </dl>
      {details.cashRoles && (
        <div className="d-flex flex-wrap gap-2 mt-3" data-testid="cash-roles">
          <Badge bg="info" text="dark">
            Cash sweep in {details.cashRoles.cashSweepAccounts} accounts
          </Badge>
          <Badge bg="info" text="dark">
            Cash split in {details.cashRoles.cashSplitAccounts} accounts
          </Badge>
        </div>
      )}
      {flags.map((flag) => (
        <Alert key={flag} variant="warning" className="mt-3 mb-0 py-2">
          {flag}
        </Alert>
      ))}
    </section>
  );
};

const WeightBars = ({ items }: { items: WeightedName[] }) =>
  items.length === 0 ? (
    <span className="text-muted">None</span>
  ) : (
    <div className="d-grid gap-2">
      {items.map((item) => (
        <div key={item.name}>
          <div className="d-flex justify-content-between small">
            <span>{item.name}</span>
            <span className="tabular-nums">{pct100(item.percent)}</span>
          </div>
          <ProgressBar now={item.percent} style={{ height: 4 }} />
        </div>
      ))}
    </div>
  );

/**
 * The Admin's form to change the firm's category of the security. It saves
 * through the mapping write, which overwrites the firm's whole row, so the
 * security's other values are sent back as they are. On an error the page
 * keeps the previous category and shows the API's message.
 */
const CategoryForm = ({ details }: { details: SecurityDetailsData }) => {
  const current = details.classification.category;
  const categories = useQueryAssetCategories();
  const [category, setCategory] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const { pushNotification } = useContext(NotificationContext);
  const { mutate, isPending } = useUpdateSecurityCategory(details.security);
  const options = categories.includes(current)
    ? categories
    : [current, ...categories];

  const save = () => {
    setError(null);
    mutate(categoryWrite(details.security, category), {
      onSuccess: () =>
        pushNotification({
          id: "security-details-category",
          header: "Category saved",
          body: `${details.security.symbol} is now in ${category}.`,
          variant: "success",
        }),
      onError: (err) => {
        setError(
          extractServerMessage(err) ?? "The category could not be saved.",
        );
        setCategory(current);
      },
    });
  };

  return (
    <Form
      className="mt-2"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <Form.Label htmlFor="security-category" className="small mb-1">
        Change category
      </Form.Label>
      <div className="d-flex gap-2">
        <Form.Select
          id="security-category"
          size="sm"
          value={category}
          disabled={isPending}
          onChange={(event) => setCategory(event.target.value)}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
        <Button
          type="submit"
          size="sm"
          disabled={isPending || category === current}
        >
          {isPending ? "Saving…" : "Save"}
        </Button>
      </div>
      {error && (
        <Alert variant="danger" className="mt-2 mb-0 py-2">
          {error}
        </Alert>
      )}
    </Form>
  );
};

const Classification = ({ details }: { details: SecurityDetailsData }) => {
  const c = details.classification;
  const { security } = details;
  const { status, roles } = useRolesResolution();
  return (
    <section>
      <h3 className="h4">Classification</h3>
      <dl className="mb-0">
        <SummaryItem label="Category">
          <div>{c.category}</div>
          <div className="small text-muted">
            {c.isFirmOverride
              ? `Firm override${c.fastTrackCategory ? ` of ${c.fastTrackCategory}` : ""}`
              : "Inherited from market data"}{" "}
            · {c.securitiesInCategory} securities share it
          </div>
        </SummaryItem>
        <SummaryItem label="ADV category">
          {security.advCategory ?? "—"}
        </SummaryItem>
        <SummaryItem label="Asset class weights">
          <WeightBars items={c.assetClasses} />
          <Link
            className="small"
            to={categoryAssetClassPath(
              c.isUncategorized ? undefined : c.category,
            )}
          >
            Edit category to asset class
          </Link>
        </SummaryItem>
        <SummaryItem label="Risk weights">
          <WeightBars items={c.riskLevels} />
          <Link
            className="small"
            to={assetClassRiskPath(c.assetClasses.map((a) => a.name))}
          >
            Edit asset class to risk
          </Link>
        </SummaryItem>
      </dl>
      {status === "ready" && roles.includes("Admin") && (
        <CategoryForm details={details} />
      )}
      {status === "error" && (
        <div className="small text-muted mt-2">
          Your roles could not be loaded, so the category can't be changed here.
        </div>
      )}
      <div className="mt-2">
        <Link to={securityMasterRowPath(security.symbol)}>
          Edit in the Security Master
        </Link>
      </div>
    </section>
  );
};

/**
 * The right-aligned number columns of the holders table. The values stay on
 * one line; a long header wraps, as ten columns do not fit the page width.
 */
const NUMBER_COLUMN = {
  headerClassName: "text-end",
  className: "text-end text-nowrap tabular-nums",
};

const holderColumnHelper = createColumnHelper<SecurityHoldingAccount>();

/** The page sizes of the holders table; the first is the default. */
const HOLDER_PAGE_SIZES = [25, 50, 100];

/** The columns of "Where it's held". Each one sorts; a missing value sorts last. */
const HOLDER_COLUMNS = [
  holderColumnHelper.accessor((h) => h.householdName ?? undefined, {
    id: "household",
    header: "Household",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) =>
      h.householdId ? (
        <Link to={`/clients/households/${h.householdId}`}>
          {h.householdName}
        </Link>
      ) : (
        "—"
      ),
  }),
  holderColumnHelper.accessor("accountName", {
    id: "account",
    header: "Account",
    cell: ({ row: { original: h } }) => (
      <>
        <Link to={`/clients/accounts/${h.accountId}`}>{h.accountName}</Link>
        <div className="small text-muted text-nowrap">{h.accountNumber}</div>
      </>
    ),
  }),
  holderColumnHelper.accessor((h) => h.registration ?? undefined, {
    id: "registration",
    header: "Registration",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) => (
      <>
        <div>{h.registration ?? "—"}</div>
        <Badge bg="light" text="dark">
          {h.taxStatus}
        </Badge>
      </>
    ),
  }),
  holderColumnHelper.accessor("units", {
    id: "units",
    header: "Units",
    cell: (info) => info.getValue().toLocaleString(),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor("value", {
    id: "value",
    header: "Value",
    cell: (info) => formatCurrency(info.getValue()),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor((h) => h.percentOfAccount ?? undefined, {
    id: "percentOfAccount",
    header: "% of account",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) => pct100(h.percentOfAccount, 2),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor((h) => h.averageCost ?? undefined, {
    id: "averageCost",
    header: "Avg cost",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) =>
      h.averageCost === null ? "—" : formatCurrency(h.averageCost, 2),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor((h) => h.unrealizedGainLoss ?? undefined, {
    id: "unrealized",
    header: "Unrealized",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) =>
      h.unrealizedGainLoss === null
        ? "—"
        : formatCurrencyComponent(h.unrealizedGainLoss),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor((h) => h.shortTermUnitsPercent ?? undefined, {
    id: "shortTermUnits",
    header: "Short-term units",
    sortUndefined: "last",
    cell: ({ row: { original: h } }) => pct100(h.shortTermUnitsPercent, 0),
    meta: NUMBER_COLUMN,
  }),
  holderColumnHelper.accessor(
    (h) =>
      h.restriction
        ? (TREATMENT_LABELS[h.restriction] ?? h.restriction)
        : undefined,
    { id: "restriction", header: "Restriction", sortUndefined: "last" },
  ),
];

type HolderFilters = {
  tax: "" | "taxable" | "deferred";
  gainLoss: "" | "loss" | "gain";
  minValue: number;
};

const Holders = ({ holdings }: { holdings: SecurityHoldingAccount[] }) => {
  const [filters, setFilters] = useState<HolderFilters>({
    tax: "",
    gainLoss: "",
    minValue: 0,
  });
  const rows = useMemo(
    () =>
      holdings.filter(
        (h) =>
          (filters.tax === "" ||
            (filters.tax === "taxable" ? h.isTaxable : !h.isTaxable)) &&
          (filters.gainLoss === "" ||
            (filters.gainLoss === "loss"
              ? (h.unrealizedGainLoss ?? 0) < 0
              : (h.unrealizedGainLoss ?? 0) > 0)) &&
          h.value >= filters.minValue,
      ),
    [holdings, filters],
  );
  const total = rows.reduce((t, h) => t + h.value, 0);
  const unrealized = rows.reduce((t, h) => t + (h.unrealizedGainLoss ?? 0), 0);
  // APP-1486 an-decision-01a158d6182d2cdd: the table sorts and pages the
  // filtered rows in the browser. The count and the totals below read
  // `rows`, so they cover every filtered row, not only the page shown.
  const { table } = useTable({
    data: rows,
    columns: HOLDER_COLUMNS,
    getRowId: (h) => String(h.accountId),
    // Page R97: a first click on any header sorts ascending. TanStack would
    // otherwise sort a number column largest first.
    sortDescFirst: false,
    enableSortingRemoval: false,
    initialState: {
      sorting: [{ id: "value", desc: true }],
      pagination: { pageIndex: 0, pageSize: HOLDER_PAGE_SIZES[0] },
    },
  });
  // Taxable accounts only, whatever the tax filter: an IRA loss is not harvestable.
  const harvest = rows.filter(
    (h) => h.isTaxable && h.lotSummary.lossAmount < 0,
  );

  return (
    <section>
      <h3 className="h4">Where it's held</h3>
      <Row className="g-2 align-items-end mb-2">
        <Col sm={3}>
          <Form.Label htmlFor="holders-tax">Tax status</Form.Label>
          <Form.Select
            id="holders-tax"
            value={filters.tax}
            onChange={(e) =>
              setFilters({
                ...filters,
                tax: e.target.value as HolderFilters["tax"],
              })
            }
          >
            <option value="">Taxable and deferred</option>
            <option value="taxable">Taxable</option>
            <option value="deferred">Tax-deferred or tax-free</option>
          </Form.Select>
        </Col>
        <Col sm={3}>
          <Form.Label htmlFor="holders-gl">Unrealized</Form.Label>
          <Form.Select
            id="holders-gl"
            value={filters.gainLoss}
            onChange={(e) =>
              setFilters({
                ...filters,
                gainLoss: e.target.value as HolderFilters["gainLoss"],
              })
            }
          >
            <option value="">Any</option>
            <option value="loss">At a loss</option>
            <option value="gain">At a gain</option>
          </Form.Select>
        </Col>
        <Col sm={3}>
          <Form.Label htmlFor="holders-min">Minimum value</Form.Label>
          <Form.Control
            id="holders-min"
            type="number"
            min={0}
            value={filters.minValue || ""}
            onChange={(e) =>
              setFilters({ ...filters, minValue: Number(e.target.value) || 0 })
            }
          />
        </Col>
        <Col sm={3} className="text-end text-muted small">
          {rows.length} of {holdings.length} accounts
        </Col>
      </Row>
      <DataTable
        table={table}
        className="table-hover mb-0"
        showSearchGroup={false}
        pageSizeOptions={HOLDER_PAGE_SIZES}
      />
      {rows.length === 0 && (
        <p className="text-muted py-2 mb-0">
          No positions match these filters.
        </p>
      )}
      <div className="d-flex flex-wrap gap-4 mt-2 small">
        <span>
          Value <b>{formatCurrency(total)}</b>
        </span>
        <span>
          Unrealized <b>{formatCurrencyComponent(unrealized)}</b>
        </span>
        <span>
          Harvestable loss{" "}
          <b>
            {formatCurrencyComponent(
              harvest.reduce((t, h) => t + h.lotSummary.lossAmount, 0),
            )}
          </b>{" "}
          in {harvest.length} taxable accounts
        </span>
      </div>
    </section>
  );
};

const Configuration = ({ details }: { details: SecurityDetailsData }) => {
  const { models, sleeves, securityGroups, categoryMapping, restrictions } =
    details.configuration;
  return (
    <section>
      <h3 className="h4">Where this security is configured</h3>
      <Row className="g-4">
        <Col lg={6}>
          <h4 className="h6">Models</h4>
          {models.length === 0 ? (
            <p className="text-muted mb-0">
              {details.security.variant === "bond"
                ? "Not in any model. Individual bonds are usually bought per household and held off-model."
                : "Not in any model."}
            </p>
          ) : (
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Model</th>
                  <th className="text-end">Direct</th>
                  <th className="text-end">Via sleeves</th>
                  <th className="text-end">Via groups</th>
                  <th className="text-end">Effective</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.modelId}>
                    <td>
                      <Link to={`/models/models/${m.modelId}`}>{m.name}</Link>
                    </td>
                    <td className="text-end">{pct100(m.direct, 2)}</td>
                    <td className="text-end">{pct100(m.viaSleeves, 2)}</td>
                    <td className="text-end">
                      {pct100(m.viaSecurityGroups, 2)}
                    </td>
                    <td className="text-end fw-semibold">
                      {pct100(m.effective, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
        <Col lg={6}>
          <h4 className="h6">Sleeves</h4>
          {sleeves.length === 0 ? (
            <p className="text-muted">Not in any sleeve.</p>
          ) : (
            <ul className="list-unstyled">
              {sleeves.map((s) => (
                <li key={s.sleeveId}>
                  <Link to={`/models/sleeves/${s.sleeveId}`}>{s.name}</Link>{" "}
                  <span className="text-muted small">
                    {s.assetCategory} · {s.region} · {pct100(s.weight, 2)} ·
                    used by {s.modelsUsing} models
                  </span>
                </li>
              ))}
            </ul>
          )}
          <h4 className="h6">Security groups</h4>
          {securityGroups.length === 0 ? (
            <p className="text-muted">Not in any security group.</p>
          ) : (
            <ul className="list-unstyled">
              {securityGroups.map((g) => (
                <li key={g.securityGroupId}>
                  <Link to={`/models/security-groups/${g.securityGroupId}`}>
                    {g.name}
                  </Link>{" "}
                  <span className="text-muted small">
                    {g.role === "primary"
                      ? `Primary · alternates ${g.alternates.map((a) => a.symbol).join(", ") || "none"}`
                      : `Alternate of ${g.primarySymbol}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <h4 className="h6">Category mapping</h4>
          {categoryMapping === null ? (
            <p className="text-muted">
              The firm maps this security to no category.
            </p>
          ) : (
            <p>
              {categoryMapping.symbol} is mapped to{" "}
              <b>{categoryMapping.category}</b>{" "}
              <Link
                className="small"
                to={securityMasterRowPath(categoryMapping.symbol)}
              >
                Edit in the Security Master
              </Link>
            </p>
          )}
          <h4 className="h6">Trading restrictions</h4>
          {restrictions.length === 0 ? (
            <p className="text-muted mb-0">
              No restrictions at model or account level.
            </p>
          ) : (
            <ul className="list-unstyled mb-0">
              {restrictions.map((r) => (
                <li key={`${r.level}-${r.id}`}>
                  <Badge bg="light" text="dark" className="me-1">
                    {r.level === "model" ? "Model" : "Account"}
                  </Badge>
                  <Link
                    to={
                      r.level === "model"
                        ? `/models/models/${r.id}`
                        : `/clients/accounts/${r.id}`
                    }
                  >
                    {r.name}
                  </Link>{" "}
                  <span className="text-muted small">
                    {TREATMENT_LABELS[r.treatment] ?? r.treatment}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Col>
      </Row>
    </section>
  );
};

const replacementOptions = (details: SecurityDetailsData) => {
  const symbols = new Set<string>();
  for (const group of details.configuration.securityGroups) {
    if (group.primarySymbol !== details.security.symbol) {
      symbols.add(group.primarySymbol);
    }
    for (const alternate of group.alternates) {
      if (alternate.symbol !== details.security.symbol) {
        symbols.add(alternate.symbol);
      }
    }
  }
  return [...symbols];
};

/** The distinct non-empty values, sorted: the options of a swap filter. */
const filterOptions = (values: (string | null)[]) =>
  [...new Set(values.filter((v): v is string => !!v?.trim()))].sort((a, b) =>
    a.localeCompare(b),
  );

const toggle = (values: string[] | undefined, value: string, on: boolean) =>
  on ? [...(values ?? []), value] : (values ?? []).filter((v) => v !== value);

/**
 * APP-1486 swap R75: the open swaps (draft, approved and executing) out of the
 * security, each linked to its swap screen. The swap section shows them above
 * the form, so the advisor sees a move already under way before starting
 * another.
 */
const OpenSwaps = ({ symbol }: { symbol: string }) => {
  const { data, isLoading, isError, error } = useQueryOpenPositionSwaps(symbol);
  if (isLoading) return <Loading />;
  if (isError) {
    return (
      <Alert variant="danger">
        {extractServerMessage(error) ??
          `The open position swaps of ${symbol} could not be loaded.`}
      </Alert>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-muted small" data-testid="open-swaps">
        No position swap out of {symbol} is open.
      </p>
    );
  }
  return (
    <div data-testid="open-swaps">
      <h4 className="h6">Open position swaps</h4>
      <Table size="sm">
        <thead>
          <tr>
            <th>Swap</th>
            <th>Replacement</th>
            <th>Status</th>
            <th className="text-end">Households</th>
            <th>Created by</th>
          </tr>
        </thead>
        <tbody>
          {data.map((swap) => (
            <tr key={swap.id}>
              <td>
                <Link to={`/rebalances/position-swaps/${swap.id}`}>
                  {swap.name}
                </Link>
              </td>
              <td className="font-monospace">
                {swap.replacementSymbol ?? "Cash"}
              </td>
              <td className="text-capitalize">{swap.status}</td>
              <td className="text-end">{swap.households}</td>
              <td>{swap.createdBy}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

/**
 * APP-1486 swap R90, R97, R113: a swap changes no model, sleeve, security
 * group or category mapping (R87). The swap section lists each one of the
 * firm that names the security, linked to the page that edits it, so the
 * advisor can stop future buys there. Category mapping writes are Admin only,
 * so a Member reads that an Admin edits them, once the roles are known and
 * only when a category mapping is listed.
 */
const ConfiguredNotice = ({ details }: { details: SecurityDetailsData }) => {
  const { status, roles } = useRolesResolution();
  const { models, sleeves, securityGroups, categoryMapping } =
    details.configuration;
  const symbol = details.security.symbol;
  const items = [
    ...models.map((m) => ({
      key: `model-${m.modelId}`,
      kind: "Model",
      name: m.name,
      to: `/models/models/${m.modelId}`,
    })),
    ...sleeves.map((s) => ({
      key: `sleeve-${s.sleeveId}`,
      kind: "Sleeve",
      name: s.name,
      to: `/models/sleeves/${s.sleeveId}`,
    })),
    ...securityGroups.map((g) => ({
      key: `group-${g.securityGroupId}`,
      kind: "Security group",
      name: g.name,
      to: `/models/security-groups/${g.securityGroupId}`,
    })),
    ...(categoryMapping === null
      ? []
      : [
          {
            key: "category-mapping",
            kind: "Category mapping",
            name: `${categoryMapping.symbol} → ${categoryMapping.category}`,
            to: securityMasterRowPath(categoryMapping.symbol),
          },
        ]),
  ];
  if (items.length === 0) return null;
  return (
    <Alert variant="warning" data-testid="swap-configured-notice">
      <p className="mb-2">
        The swap does not change where the firm names {symbol}. To stop future
        buys of {symbol}, edit each of these on its own page:
      </p>
      <ul className="mb-0">
        {items.map((item) => (
          <li key={item.key}>
            {item.kind}: <Link to={item.to}>{item.name}</Link>
          </li>
        ))}
      </ul>
      {categoryMapping !== null &&
        status === "ready" &&
        !roles.includes("Admin") && (
          <p className="mt-2 mb-0 small">
            Only an Admin edits category mappings in the Security Master.
          </p>
        )}
    </Alert>
  );
};

const MoveOut = ({
  details,
  opened,
}: {
  details: SecurityDetailsData;
  /** Changes on each click of the swap button, which brings this into view. */
  opened: number;
}) => {
  const symbol = details.security.symbol;
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (opened === 0) return;
    // jsdom has no scrollIntoView.
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth" });
    sectionRef.current?.focus({ preventScroll: true });
  }, [opened]);
  const { pushNotification } = useContext(NotificationContext);
  const notifyError = (error: Error) =>
    pushNotification({
      id: "security-move-out-error",
      header: "Move-out failed",
      body: error.message,
      variant: "danger",
    });
  const suggestions = replacementOptions(details);
  const accountTypeOptions = filterOptions(
    details.holdings.map((h) => h.registration),
  );
  const custodianOptions = filterOptions(
    details.holdings.map((h) => h.custodian),
  );
  const [request, setRequest] = useState<MoveOutRequest>({
    replacementSymbol: suggestions[0] ?? null,
    sellTaxDeferred: true,
    taxable: "losses_only",
    maxGainPerHousehold: 1000,
    // The wash-sale rule always applies; the form has no control for it, but
    // the request requires the field.
    avoidWashSales: true,
    shareClassExchange: false,
    // None checked: every account.
    accountTypes: [],
    custodians: [],
  });
  const [preview, setPreview] = useState<MoveOutPreview | null>(null);
  const [created, setCreated] = useState<number | null>(null);
  const previewMutation = usePreviewMoveOut(symbol);
  const createMutation = useCreatePositionSwap();

  const update = (change: Partial<MoveOutRequest>) => {
    setRequest({ ...request, ...change });
    setPreview(null);
    setCreated(null);
  };

  const runPreview = () =>
    previewMutation.mutate(request, {
      onSuccess: setPreview,
      onError: notifyError,
    });

  const runCreate = () =>
    createMutation.mutate(
      {
        symbol,
        name: `${symbol} → ${request.replacementSymbol || "cash"}, ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`,
        rules: request,
      },
      {
        onSuccess: ({ id, households }) => {
          setCreated(id);
          pushNotification({
            id: "security-move-out-created",
            header: "Position swap saved",
            body: `Draft saved with ${households} household proposals.`,
            variant: "success",
          });
        },
        onError: notifyError,
      },
    );

  const selling = preview?.accounts.filter((a) => a.action !== "hold") ?? [];
  // The preview names each account's tax status as "Where it's held" does.
  const taxStatusOf = new Map(
    details.holdings.map((h) => [h.accountId, h.taxStatus]),
  );

  return (
    <div id="move-out" ref={sectionRef} tabIndex={-1}>
      <OpenSwaps symbol={symbol} />
      <h3 className="h4">Move clients out of {symbol}</h3>
      <p className="text-muted small">
        Sells the position in each account by the rules below, buys the
        replacement in the same account, and leaves the rest where it is.
        Taxable accounts sell by the taxable rule and the wash-sale rule, and
        the preview shows what each sale realizes. It proposes one Position swap
        per household; approving and executing them produces the custodian trade
        files, as for any rebalance.
      </p>
      <ConfiguredNotice details={details} />
      {/* The form on top, the preview under it at the full width, so the
            preview table has room for its columns. */}
      <Row className="g-4">
        <Col lg={6} xl={5}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              runPreview();
            }}
            className="d-grid gap-3"
          >
            <div>
              <Form.Label htmlFor="move-out-replacement">
                Replace with
              </Form.Label>
              <Form.Control
                id="move-out-replacement"
                list="move-out-suggestions"
                placeholder="Symbol, or leave blank to hold cash"
                value={request.replacementSymbol ?? ""}
                onChange={(e) =>
                  update({
                    replacementSymbol:
                      e.target.value.trim().toUpperCase() || null,
                  })
                }
              />
              <datalist id="move-out-suggestions">
                {suggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              {suggestions.length > 0 && (
                <Form.Text>
                  From this security's groups: {suggestions.join(", ")}
                </Form.Text>
              )}
            </div>
            <Form.Check
              id="move-out-share-class"
              label="The replacement is another share class of the same fund"
              checked={request.shareClassExchange === true}
              onChange={(e) => update({ shareClassExchange: e.target.checked })}
            />
            <Form.Check
              id="move-out-deferred"
              label="Sell all of it in tax-deferred and tax-free accounts"
              checked={request.sellTaxDeferred}
              onChange={(e) => update({ sellTaxDeferred: e.target.checked })}
            />
            <fieldset className="border rounded p-2">
              <legend className="small text-muted w-auto px-1 mb-0">
                Taxable accounts
              </legend>
              {(
                [
                  ["losses_only", "Sell only lots at a loss"],
                  [
                    "gain_limit",
                    "Sell while each household's net gain stays under",
                  ],
                  ["all", "Sell everything, realizing gains"],
                  ["none", "Leave taxable accounts alone"],
                ] as const
              ).map(([value, label]) => (
                <div key={value} className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="radio"
                    id={`move-out-taxable-${value}`}
                    name="move-out-taxable"
                    label={label}
                    checked={request.taxable === value}
                    onChange={() => update({ taxable: value })}
                  />
                  {value === "gain_limit" && (
                    <Form.Control
                      aria-label="Gain limit per household"
                      type="number"
                      min={0}
                      step={500}
                      size="sm"
                      style={{ width: 110 }}
                      value={request.maxGainPerHousehold ?? 0}
                      onChange={(e) =>
                        update({
                          maxGainPerHousehold: Number(e.target.value) || 0,
                        })
                      }
                    />
                  )}
                </div>
              ))}
            </fieldset>
            {(accountTypeOptions.length > 0 || custodianOptions.length > 0) && (
              <fieldset className="border rounded p-2">
                <legend className="small text-muted w-auto px-1 mb-0">
                  Accounts
                </legend>
                <Form.Text className="d-block mb-1">
                  Check none to include every account.
                </Form.Text>
                {(
                  [
                    [
                      "accountTypes",
                      "Account types",
                      "move-out-type",
                      accountTypeOptions,
                    ],
                    [
                      "custodians",
                      "Custodians",
                      "move-out-custodian",
                      custodianOptions,
                    ],
                  ] as const
                ).map(
                  ([field, label, idPrefix, options]) =>
                    options.length > 0 && (
                      <div
                        key={field}
                        role="group"
                        aria-label={label}
                        className="mb-1"
                      >
                        <div className="small fw-semibold">{label}</div>
                        {options.map((option, i) => (
                          <Form.Check
                            key={option}
                            inline
                            id={`${idPrefix}-${i}`}
                            label={option}
                            checked={(request[field] ?? []).includes(option)}
                            onChange={(e) =>
                              update({
                                [field]: toggle(
                                  request[field],
                                  option,
                                  e.target.checked,
                                ),
                              })
                            }
                          />
                        ))}
                      </div>
                    ),
                )}
              </fieldset>
            )}
            <div>
              <Button type="submit" disabled={previewMutation.isPending}>
                Preview trades
              </Button>
            </div>
          </Form>
        </Col>
        <Col xs={12}>
          {!preview ? (
            <p className="text-muted">
              Preview the trades to see what each account sells, what it
              realizes, and what stays.
            </p>
          ) : (
            <>
              <Row>
                <Col md={8} lg={6} xl={5}>
                  <dl className="mb-3">
                    <SummaryItem label="Accounts that sell">
                      {preview.totals.accountsSelling} of{" "}
                      {preview.accounts.length}
                    </SummaryItem>
                    <SummaryItem
                      label={
                        preview.replacement
                          ? `Moves into ${preview.replacement.symbol}`
                          : "Raised as cash"
                      }
                    >
                      {formatCurrency(preview.totals.proceeds)}
                    </SummaryItem>
                    <SummaryItem
                      label={
                        preview.totals.realizedGain < 0
                          ? "Losses harvested"
                          : "Taxable gain realized"
                      }
                    >
                      {formatCurrencyComponent(preview.totals.realizedGain)}
                    </SummaryItem>
                    <SummaryItem label="Stays put">
                      {formatCurrency(preview.totals.remainingValue)}
                    </SummaryItem>
                  </dl>
                </Col>
              </Row>
              <div className="table-responsive">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th className="text-end text-nowrap">Sell</th>
                      <th className="text-nowrap">Buy</th>
                      <th className="text-end text-nowrap">Gain / loss</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.accounts.map((a) => (
                      <tr
                        key={a.accountId}
                        className={a.action === "hold" ? "text-muted" : ""}
                      >
                        <td>
                          {a.householdName} · {a.accountName}{" "}
                          <Badge bg="light" text="dark">
                            {taxStatusOf.get(a.accountId) ??
                              (a.isTaxable ? "Taxable" : "Deferred")}
                          </Badge>
                        </td>
                        <td className="text-end text-nowrap tabular-nums">
                          {a.action === "hold"
                            ? "—"
                            : a.sellUnits.toLocaleString()}
                        </td>
                        <td className="text-nowrap tabular-nums">
                          {a.buy
                            ? `${a.buy.units.toLocaleString()} ${a.buy.symbol}`
                            : "—"}
                        </td>
                        <td className="text-end text-nowrap tabular-nums">
                          {a.action === "hold"
                            ? ""
                            : a.realizedGain === null
                              ? a.isTaxable
                                ? "unknown"
                                : "not taxed"
                              : formatCurrencyComponent(a.realizedGain)}
                          {a.estimatedGain && a.action !== "hold" && " (est.)"}
                        </td>
                        <td className="small">
                          {MOVE_OUT_REASON_LABELS[a.reason]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Button
                  onClick={runCreate}
                  disabled={
                    selling.length === 0 ||
                    createMutation.isPending ||
                    created !== null
                  }
                >
                  Save as a position swap for{" "}
                  {new Set(selling.map((a) => a.householdId)).size} households
                </Button>
                {created !== null && (
                  <span className="small">
                    Saved as a{" "}
                    <Link to={`/rebalances/position-swaps/${created}`}>
                      draft position swap
                    </Link>
                    . Its household proposals are on{" "}
                    <Link to="/rebalances">Trade Proposals</Link>, typed
                    Position swap.
                  </span>
                )}
              </div>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

/**
 * The default range of the Activity tab (page D5): one year before `today`
 * to `today`, as `YYYY-MM-DD`.
 */
export function defaultActivityRange(today: Date) {
  const yearAgo = new Date(today);
  yearAgo.setFullYear(today.getFullYear() - 1);
  return {
    startDate: toISODateNoTimezone(yearAgo),
    endDate: toISODateNoTimezone(today),
  };
}

/**
 * Why the Activity tab cannot ask for the dates, or `null` when it can. The
 * route refuses a start after the end; the tab says so before asking.
 */
function activityRangeProblem(startDate: string, endDate: string) {
  if (!startDate || !endDate) return "Enter a start date and an end date.";
  if (startDate > endDate) {
    return `The start date ${startDate} is after the end date ${endDate}.`;
  }
  return null;
}

/**
 * APP-1486 page R68, R71: the firm's transactions in the security from the
 * start date to the end date, one year ago to today by default (D5), with
 * their totals under the list.
 */
const Activity = ({ symbol }: { symbol: string }) => {
  const [range, setRange] = useState(() => defaultActivityRange(new Date()));
  const problem = activityRangeProblem(range.startDate, range.endDate);
  const { data, isLoading, isError, error } = useQuerySecurityTransactions(
    symbol,
    range.startDate,
    range.endDate,
    problem === null,
  );

  return (
    <>
      <Row className="g-3 mb-3">
        <Col sm={6} md={4} lg={3}>
          <Form.Group controlId="activity-start-date">
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="date"
              value={range.startDate}
              onChange={(e) =>
                setRange((r) => ({ ...r, startDate: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
        <Col sm={6} md={4} lg={3}>
          <Form.Group controlId="activity-end-date">
            <Form.Label>End date</Form.Label>
            <Form.Control
              type="date"
              value={range.endDate}
              onChange={(e) =>
                setRange((r) => ({ ...r, endDate: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
      </Row>
      {problem !== null ? (
        <Alert variant="warning">{problem}</Alert>
      ) : isError ? (
        <Alert variant="danger">
          {extractServerMessage(error) ??
            "The transactions could not be loaded."}
        </Alert>
      ) : (
        <>
          <TransactionsTable
            additionalColumns={ACTIVITY_COLUMNS}
            isLoading={isLoading}
            isError={false}
            transactions={data?.transactions ?? []}
            excludeCashSweepByDefault={false}
          />
          {data && (
            <Row className="mt-3" data-testid="activity-totals">
              <Col md={8} lg={6} xl={4}>
                <dl className="mb-0">
                  <SummaryItem label="Total bought">
                    {formatCurrency(data.totals.bought, 2)}
                  </SummaryItem>
                  <SummaryItem label="Total sold">
                    {formatCurrency(data.totals.sold, 2)}
                  </SummaryItem>
                  <SummaryItem label="Total income">
                    {formatCurrency(data.totals.income, 2)}
                  </SummaryItem>
                </dl>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const activityColumnHelper = createColumnHelper<Transaction>();

/** The account and household of each transaction, linked. */
const ACTIVITY_COLUMNS = [
  activityColumnHelper.accessor("accountNumber", {
    cell: (info) => (
      <Link to={`/clients/accounts/${info.row.original.accountId}`}>
        {info.getValue()}
      </Link>
    ),
    header: () => "Account Number",
    enableColumnFilter: false,
  }),
  activityColumnHelper.accessor((row) => row.householdName ?? "", {
    id: "householdName",
    cell: (info) => {
      const { householdId } = info.row.original;
      return householdId === null ? (
        "—"
      ) : (
        <Link to={`/clients/households/${householdId}`}>{info.getValue()}</Link>
      );
    },
    header: () => "Household",
    enableColumnFilter: false,
  }),
];

export default SecurityDetails;
